import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Star, 
  QrCode, 
  MessageSquare, 
  BarChart3, 
  Zap, 
  Shield, 
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  TrendingUp,
  FileText,
  Mail,
  Phone,
  Sparkles
} from "lucide-react";

// Dashboard Carousel Component
const DashboardCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "Tableau de bord",
      content: (
        <div className="p-6 bg-slate-50">
          <div className="flex gap-6">
            {/* Sidebar Mini */}
            <div className="hidden md:block w-48 bg-slate-900 rounded-xl p-4 text-white flex-shrink-0">
              <div className="text-lg font-bold mb-6">Respons<span className="text-violet-400">IA</span></div>
              <div className="space-y-2 text-sm">
                <div className="bg-violet-600/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Tableau de bord
                </div>
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <MessageSquare className="w-4 h-4" />
                  Avis
                </div>
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <QrCode className="w-4 h-4" />
                  QR Code
                </div>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="flex-1 space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Avis collectés</div>
                  <div className="text-2xl font-bold text-slate-900">127</div>
                  <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +12 cette semaine
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Note moyenne</div>
                  <div className="text-2xl font-bold text-slate-900">4.7<span className="text-sm text-slate-400">/5</span></div>
                  <div className="flex gap-0.5 mt-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-3 h-3 ${i <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-400'}`} />
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Avis positifs</div>
                  <div className="text-2xl font-bold text-emerald-600">89%</div>
                  <div className="text-xs text-slate-400 mt-1">113 avis ≥4★</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">À traiter</div>
                  <div className="text-2xl font-bold text-amber-600">3</div>
                  <div className="text-xs text-slate-400 mt-1">Avis négatifs</div>
                </div>
              </div>
              
              {/* Reviews List */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-sm font-medium text-slate-700 mb-3">Derniers avis reçus</div>
                <div className="space-y-3">
                  {[
                    { name: "Marie D.", rating: 5, text: "Excellent service, je recommande vivement !", time: "Il y a 2h" },
                    { name: "Pierre L.", rating: 4, text: "Très satisfait de ma visite", time: "Il y a 5h" },
                    { name: "Sophie M.", rating: 5, text: "Personnel très agréable", time: "Hier" }
                  ].map((review, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        {review.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-900">{review.name}</span>
                          <span className="text-xs text-slate-400">{review.time}</span>
                        </div>
                        <div className="flex gap-0.5 my-1">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3 h-3 ${s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-slate-600">{review.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "QR Code",
      content: (
        <div className="p-6 bg-slate-50">
          <div className="flex gap-6">
            {/* Sidebar Mini */}
            <div className="hidden md:block w-48 bg-slate-900 rounded-xl p-4 text-white flex-shrink-0">
              <div className="text-lg font-bold mb-6">Respons<span className="text-violet-400">IA</span></div>
              <div className="space-y-2 text-sm">
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <BarChart3 className="w-4 h-4" />
                  Tableau de bord
                </div>
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <MessageSquare className="w-4 h-4" />
                  Avis
                </div>
                <div className="bg-violet-600/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <QrCode className="w-4 h-4" />
                  QR Code
                </div>
              </div>
            </div>
            
            {/* QR Code Content */}
            <div className="flex-1">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  {/* QR Code Display */}
                  <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-6 border border-violet-100">
                    <div className="w-40 h-40 bg-white rounded-xl p-3 shadow-sm">
                      <div className="w-full h-full bg-slate-900 rounded-lg flex items-center justify-center">
                        <QrCode className="w-24 h-24 text-white" />
                      </div>
                    </div>
                    <p className="text-center text-sm text-violet-700 font-medium mt-3">Votre QR Code unique</p>
                  </div>
                  
                  {/* Instructions */}
                  <div className="flex-1 space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900">Comment ça marche ?</h3>
                    <div className="space-y-3">
                      {[
                        { step: "1", text: "Le client scanne le QR code" },
                        { step: "2", text: "Il note son expérience de 1 à 5 étoiles" },
                        { step: "3", text: "Si 4+ étoiles → Redirection Google Avis" },
                        { step: "4", text: "Si moins → Avis privé pour amélioration" }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-xs font-bold">
                            {item.step}
                          </div>
                          <span className="text-sm text-slate-600">{item.text}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <div className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium">
                        Télécharger
                      </div>
                      <div className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700">
                        Imprimer
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Plan d'action IA",
      content: (
        <div className="p-6 bg-slate-50">
          <div className="flex gap-6">
            {/* Sidebar Mini */}
            <div className="hidden md:block w-48 bg-slate-900 rounded-xl p-4 text-white flex-shrink-0">
              <div className="text-lg font-bold mb-6">Respons<span className="text-violet-400">IA</span></div>
              <div className="space-y-2 text-sm">
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <BarChart3 className="w-4 h-4" />
                  Tableau de bord
                </div>
                <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
                  <MessageSquare className="w-4 h-4" />
                  Avis
                </div>
                <div className="bg-violet-600/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Plan d'action
                </div>
              </div>
            </div>
            
            {/* Action Plan Content */}
            <div className="flex-1 space-y-4">
              {/* Header */}
              <div className="bg-gradient-to-r from-violet-500 to-pink-500 rounded-xl p-4 text-white">
                <div className="flex items-center gap-3">
                  <Lightbulb className="w-8 h-8" />
                  <div>
                    <h3 className="font-semibold">Plan d'action IA</h3>
                    <p className="text-sm text-white/80">Basé sur l'analyse de vos avis négatifs</p>
                  </div>
                </div>
              </div>
              
              {/* Recommendations */}
              <div className="grid gap-3">
                {[
                  { title: "Réduire le temps d'attente", desc: "Former l'équipe sur la gestion des flux", priority: "Haute", cost: "0€" },
                  { title: "Améliorer l'accueil client", desc: "Session de formation service client", priority: "Moyenne", cost: "200€" },
                  { title: "Suivi des commandes", desc: "Mettre en place un système de tracking", priority: "Basse", cost: "50€" }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">{item.title}</h4>
                        <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          item.priority === 'Haute' ? 'bg-red-100 text-red-700' :
                          item.priority === 'Moyenne' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {item.priority}
                        </span>
                        <p className="text-sm font-medium text-slate-900 mt-2">{item.cost}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);
  
  return (
    <div className="relative">
      {/* Slides */}
      <div className="overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className="w-full flex-shrink-0">
              {slide.content}
            </div>
          ))}
        </div>
      </div>
      
      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-300 ${
              currentSlide === index 
                ? 'w-8 h-2 bg-violet-600 rounded-full' 
                : 'w-2 h-2 bg-slate-300 rounded-full hover:bg-slate-400'
            }`}
          />
        ))}
      </div>
      
      {/* Slide Labels */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 border border-slate-200">
        {slides[currentSlide].title}
      </div>
    </div>
  );
};

// Pricing Section Component with Toggle
const PricingSection = () => {
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const plan = {
    name: "Starter",
    description: "Tout ce qu'il faut pour booster vos avis",
    monthlyPrice: 49,
    annualPrice: 490,
    features: [
      "QR Code personnalisé illimité",
      "Filtrage intelligent des avis",
      "Redirection vers Google automatique",
      "Tableau de bord & statistiques",
      "Plan d'action IA personnalisé",
      "Rapports hebdomadaires par email",
      "Support prioritaire"
    ]
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
          Un tarif simple et transparent
        </h2>
        <p className="text-lg text-slate-600 mb-8">
          {billingPeriod === "monthly" 
            ? "3 jours d'essai gratuit. Sans engagement, annulable à tout moment."
            : "Économisez 98€ avec l'abonnement annuel. Engagement 12 mois."
          }
        </p>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-100 p-1.5 rounded-full inline-flex items-center">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                billingPeriod === "monthly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all relative ${
                billingPeriod === "annual"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Annuel
              <span className="absolute -top-2 -right-3 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                -17%
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <div 
          className="relative bg-white rounded-2xl p-8 max-w-md w-full border-2 border-violet-500 shadow-xl shadow-violet-500/10"
          data-testid="pricing-card-starter"
        >
          {/* Badge */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center px-4 py-1 bg-gradient-to-r from-violet-600 to-pink-500 text-white text-sm font-medium rounded-full">
              {billingPeriod === "annual" ? "2 mois offerts" : "Recommandé"}
            </span>
          </div>

          {/* Plan Header */}
          <div className="mb-6 mt-2">
            <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
            <p className="text-slate-500 mt-1">{plan.description}</p>
          </div>

          {/* Price */}
          <div className="mb-6">
            {billingPeriod === "annual" ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-slate-900">490€</span>
                  <span className="text-slate-500">/an TTC</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-slate-400 line-through text-lg">588€</span>
                  <span className="text-emerald-600 font-semibold text-sm bg-emerald-50 px-2 py-0.5 rounded-full">
                    Économisez 98€
                  </span>
                </div>
                <p className="text-slate-500 text-sm mt-1">
                  Soit 40,83€/mois · Engagement 12 mois
                </p>
              </>
            ) : (
              <>
                <span className="text-5xl font-bold text-slate-900">{plan.monthlyPrice}€</span>
                <span className="text-slate-500">/mois TTC</span>
                <p className="text-slate-500 text-sm mt-1">Sans engagement</p>
              </>
            )}
          </div>

          {/* Annual Bonus Card */}
          {billingPeriod === "annual" && (
            <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-xl p-4 mb-6 border border-violet-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">2 mois offerts</p>
                  <p className="text-slate-500 text-xs">Payez 10 mois, profitez 12 mois</p>
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          <ul className="space-y-3 mb-8">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-3 text-slate-600">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Link to="/signup">
            <Button 
              className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
              data-testid="pricing-starter-cta"
            >
              {billingPeriod === "annual" 
                ? "S'abonner pour 490€/an" 
                : "Commencer l'essai gratuit"
              }
            </Button>
          </Link>

          {/* Trust badge */}
          <p className="text-center text-xs text-slate-400 mt-4">
            {billingPeriod === "annual" 
              ? "Paiement sécurisé · Accès immédiat"
              : "3 jours gratuits · Sans carte bancaire"
            }
          </p>
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const features = [
    {
      icon: QrCode,
      title: "QR Code Personnalisé",
      description: "Générez un QR code unique pour votre établissement et collectez des avis facilement."
    },
    {
      icon: MessageSquare,
      title: "Gestion Intelligente",
      description: "Triez automatiquement les avis positifs et négatifs pour optimiser votre e-réputation."
    },
    {
      icon: BarChart3,
      title: "KPIs & Tableau de bord",
      description: "Suivez vos indicateurs clés de performance et l'évolution de vos avis en temps réel."
    },
    {
      icon: Zap,
      title: "Plan d'Action IA",
      description: "Recevez des recommandations concrètes basées sur l'analyse de vos avis négatifs."
    }
  ];

  const businessBenefits = [
    {
      icon: TrendingUp,
      title: "Boostez votre visibilité",
      subtitle: "+68% de visibilité Google",
      description: "Plus d'avis = meilleur référencement local. Apparaissez en tête des résultats Google Maps et attirez plus de clients.",
      color: "emerald"
    },
    {
      icon: Star,
      title: "Attirez de nouveaux clients",
      subtitle: "93% lisent les avis avant d'acheter",
      description: "Une note élevée et des avis récents rassurent vos futurs clients et les incitent à vous choisir plutôt que vos concurrents.",
      color: "amber"
    },
    {
      icon: Lightbulb,
      title: "Améliorez en continu",
      subtitle: "Avis négatifs → Actions concrètes",
      description: "Les retours clients négatifs restent privés et sont transformés en plan d'action pour améliorer votre service en interne.",
      color: "violet"
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "49",
      description: "Tout ce qu'il faut pour gérer votre e-réputation",
      features: [
        "3 jours d'essai gratuit",
        "QR code personnalisé",
        "Collecte d'avis illimitée",
        "Redirection automatique Google (≥4★)",
        "Tableau de bord & KPIs",
        "Plan d'action basé sur vos avis",
        "Alertes avis négatifs (<4 étoiles)",
        "Support par email"
      ],
      cta: "Commencer l'essai gratuit",
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">
                Respons<span className="text-violet-600">IA</span>
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600" data-testid="login-link">
                  Connexion
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="signup-link">
                  Commencer gratuitement
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-white to-pink-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              <span>3 jours d'essai gratuit • Sans engagement</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] leading-tight mb-6">
              Transformez vos clients en{" "}
              <span className="bg-gradient-to-r from-violet-600 to-pink-500 bg-clip-text text-transparent">
                ambassadeurs
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              <strong>Plus de visibilité, plus de clients, plus de chiffre d'affaires.</strong><br/>
              Collectez des avis Google positifs automatiquement et transformez les retours négatifs en opportunités d'amélioration.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90 rounded-full px-8 h-12 text-base shadow-lg shadow-violet-500/25"
                  data-testid="hero-cta"
                >
                  Essai gratuit 3 jours
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-full px-8 h-12 text-base border-slate-300"
                >
                  J'ai déjà un compte
                </Button>
              </Link>
            </div>
            <p className="text-sm text-slate-500 mt-4">Sans engagement • Annulable à tout moment</p>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: "+68%", label: "Visibilité Google" },
              { value: "x3", label: "Plus d'avis collectés" },
              { value: "93%", label: "Clients lisent les avis" },
              { value: "100%", label: "Avis négatifs filtrés" }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4">
                <div className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Dashboard Mockup Carousel */}
          <div className="mt-16 relative max-w-5xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 to-pink-500/20 rounded-3xl blur-2xl"></div>
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-300/50 border border-slate-200 overflow-hidden">
              {/* Browser Chrome */}
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-white rounded-lg px-4 py-1.5 text-sm text-slate-500 max-w-md mx-auto border border-slate-200">
                    app.responsia.fr/dashboard
                  </div>
                </div>
              </div>
              
              {/* Carousel Container */}
              <DashboardCarousel />
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border border-slate-200 animate-bounce-slow hidden lg:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-900">Nouvel avis 5★</div>
                  <div className="text-[10px] text-slate-500">Redirigé vers Google</div>
                </div>
              </div>
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-slate-200 hidden lg:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-900">Plan d'action IA</div>
                  <div className="text-[10px] text-slate-500">3 recommandations</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4" />
              <span>Retour sur investissement prouvé</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Augmentez votre chiffre d'affaires
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Chaque avis positif est une opportunité de conquérir de nouveaux clients. 
              Chaque avis négatif est une chance de vous améliorer.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {businessBenefits.map((benefit, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-8 border-2 transition-all duration-300 hover:scale-105 ${
                  benefit.color === 'emerald' 
                    ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400' 
                    : benefit.color === 'amber'
                    ? 'bg-amber-50 border-amber-200 hover:border-amber-400'
                    : 'bg-violet-50 border-violet-200 hover:border-violet-400'
                }`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                  benefit.color === 'emerald' 
                    ? 'bg-emerald-500' 
                    : benefit.color === 'amber'
                    ? 'bg-amber-500'
                    : 'bg-violet-500'
                }`}>
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className={`text-sm font-semibold mb-3 ${
                  benefit.color === 'emerald' 
                    ? 'text-emerald-600' 
                    : benefit.color === 'amber'
                    ? 'text-amber-600'
                    : 'text-violet-600'
                }`}>
                  {benefit.subtitle}
                </p>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* ROI Calculator Preview */}
          <div className="mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Le calcul est simple
                </h3>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  Un client satisfait en parle à 3 personnes. Un avis Google positif est visible par des centaines de prospects chaque mois.
                </p>
                <ul className="space-y-3">
                  {[
                    "1 avis 5★ = 10 nouveaux clients potentiels",
                    "Note Google ≥ 4.5 = +25% de visites",
                    "Avis récents = confiance renforcée"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Abonnement mensuel</p>
                  <div className="text-4xl font-bold mb-1">49€</div>
                  <p className="text-slate-400 text-sm mb-4">soit 1,60€/jour</p>
                  <div className="border-t border-white/20 pt-4 mt-4">
                    <p className="text-emerald-400 font-semibold">Rentabilisé dès le 1er nouveau client</p>
                    <p className="text-slate-400 text-sm mt-1">grâce à vos avis Google</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une solution complète pour gérer votre réputation en ligne et améliorer l'expérience client.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-slate-50 rounded-2xl p-6 hover:bg-white hover:shadow-lg hover:shadow-slate-200/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Comment ça fonctionne ?
            </h2>
            <p className="text-lg text-slate-600">
              En 3 étapes simples, optimisez votre e-réputation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Affichez votre QR code",
                description: "Placez votre QR code au comptoir, en vitrine ou sur vos supports de communication."
              },
              {
                step: "2",
                title: "Collectez les avis",
                description: "Vos clients scannent le code et partagent leur expérience en quelques secondes."
              },
              {
                step: "3",
                title: "Optimisez votre réputation",
                description: "Les avis positifs vont sur Google, les retours négatifs restent privés pour amélioration."
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 border border-slate-100 h-full">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-pink-500 rounded-full flex items-center justify-center text-white font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-slate-300 -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white" id="pricing">
        <PricingSection />
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-indigo-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Plus_Jakarta_Sans'] mb-4">
            Prêt à transformer vos avis en atout ?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Rejoignez des centaines de professionnels qui font confiance à ResponsIA.
          </p>
          <Link to="/signup">
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 h-12"
            >
              Commencer maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-8">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-400" />
              <span className="font-semibold text-white text-lg">ResponsIA</span>
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="mailto:contact@responsia.fr" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                contact@responsia.fr
              </a>
              <a href="tel:+33666027925" className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                06 66 02 79 25
              </a>
            </div>
            
            {/* Links */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/a-propos" className="hover:text-white transition-colors">
                À propos
              </Link>
              <Link to="/faq" className="hover:text-white transition-colors">
                FAQ
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                Contact
              </Link>
              <Link to="/cgv" className="hover:text-white transition-colors">
                CGV
              </Link>
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/cgu" className="hover:text-white transition-colors">
                CGU
              </Link>
              <Link to="/confidentialite" className="hover:text-white transition-colors">
                Confidentialité
              </Link>
              <Link to="/cookies" className="hover:text-white transition-colors">
                Cookies
              </Link>
              <Link to="/rgpd" className="hover:text-white transition-colors">
                RGPD
              </Link>
              <button 
                onClick={() => {
                  window.dispatchEvent(new Event('openCookieSettings'));
                }}
                className="hover:text-white transition-colors"
                data-testid="manage-cookies-btn"
              >
                Gérer les cookies
              </button>
            </div>
            
            {/* Copyright */}
            <p className="text-sm text-center">
              © {new Date().getFullYear()} ResponsIA. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
